import React, { useEffect } from 'react';
import { Dialog } from 'sos-common/js/components/Dialog/Dialog';
import { Icon } from 'sos-common/js/components/Icon/icon';
import { makeTranslator } from 'sos-common/js/utils/translations';
import { doPost, doGet } from 'sos-eservice-common/js/services';
import { ResponseError } from 'sos-common/js/utils/ResponseError';
import { ISwishResponse, PaymentStatus, PollResponse } from './swishresponse.types';
import { AnsokanType, Swish, SwishM } from '../StepTwoSubmitButton/StepTwoSubmitButton.types';
import { ResourceItem } from 'sos-common/js/commonTypes/resourceItem';
import { CountryIso2, ParsedCountry, PhoneInput, defaultCountries, parseCountry } from 'react-international-phone';
import LoadingSpinner from 'sos-common/js/components/LoadingSpinner/LoadingSpinner';
import { EducationLocation } from '../../models/ansokan.model';
import { SpecialistbevisTyp } from '../AnsokanForms/Specialistbevis/SpecialistbevisAnsokanForm.types';

export interface SwishPaymentDialogProps {
  transactionId?: string;
  pollLink: string;
  preSwishRedirect: boolean;
  swishPaymentLink: string;
  ansokanType: AnsokanType;
  ansokanId: string;
  validationPath: string;
  header: string;
  translations: ResourceItem[];
  educationLocation?: EducationLocation;
  specialistBevisTyp?: SpecialistbevisTyp;
  completedSupplementaryEducation?: boolean;
  paidAssessmentFeeEarlier?: boolean;
  onDialogClose: () => void;
  onHealthCheckFail: () => void;
  onValidationFail: (msg: string) => void;
}

export interface CountryMap {
  iso2: CountryIso2;
  src: string;
}

export function SwishPaymentDialog(props: SwishPaymentDialogProps) {
  const maxTicks = props.preSwishRedirect ? 250 : 60;
  const countryList: CountryMap[] = [
    { iso2: 'se', src: '/ClientSource/gfx/flags/se.svg' },
    { iso2: 'at', src: '/ClientSource/gfx/flags/at.svg' },
    { iso2: 'ad', src: '/ClientSource/gfx/flags/ad.svg' },
    { iso2: 'al', src: '/ClientSource/gfx/flags/al.svg' },
    { iso2: 'am', src: '/ClientSource/gfx/flags/am.svg' },
    { iso2: 'az', src: '/ClientSource/gfx/flags/az.svg' },
    { iso2: 'be', src: '/ClientSource/gfx/flags/be.svg' },
    { iso2: 'bg', src: '/ClientSource/gfx/flags/bg.svg' },
    { iso2: 'bi', src: '/ClientSource/gfx/flags/bi.svg' },
    { iso2: 'by', src: '/ClientSource/gfx/flags/by.svg' },
    { iso2: 'ch', src: '/ClientSource/gfx/flags/ch.svg' },
    { iso2: 'cy', src: '/ClientSource/gfx/flags/cy.svg' },
    { iso2: 'cz', src: '/ClientSource/gfx/flags/cz.svg' },
    { iso2: 'de', src: '/ClientSource/gfx/flags/de.svg' },
    { iso2: 'dk', src: '/ClientSource/gfx/flags/dk.svg' },
    { iso2: 'ee', src: '/ClientSource/gfx/flags/ee.svg' },
    { iso2: 'es', src: '/ClientSource/gfx/flags/es.svg' },
    { iso2: 'fi', src: '/ClientSource/gfx/flags/fi.svg' },
    { iso2: 'fr', src: '/ClientSource/gfx/flags/fr.svg' },
    { iso2: 'gb', src: '/ClientSource/gfx/flags/gb.svg' },
    { iso2: 'ge', src: '/ClientSource/gfx/flags/ge.svg' },
    { iso2: 'gr', src: '/ClientSource/gfx/flags/gr.svg' },
    { iso2: 'hr', src: '/ClientSource/gfx/flags/hr.svg' },
    { iso2: 'hu', src: '/ClientSource/gfx/flags/hu.svg' },
    { iso2: 'ie', src: '/ClientSource/gfx/flags/ie.svg' },
    { iso2: 'is', src: '/ClientSource/gfx/flags/is.svg' },
    { iso2: 'it', src: '/ClientSource/gfx/flags/it.svg' },
    { iso2: 'kz', src: '/ClientSource/gfx/flags/kz.svg' },
    { iso2: 'li', src: '/ClientSource/gfx/flags/li.svg' },
    { iso2: 'lt', src: '/ClientSource/gfx/flags/lt.svg' },
    { iso2: 'lu', src: '/ClientSource/gfx/flags/lu.svg' },
    { iso2: 'lv', src: '/ClientSource/gfx/flags/lv.svg' },
    { iso2: 'mc', src: '/ClientSource/gfx/flags/mc.svg' },
    { iso2: 'md', src: '/ClientSource/gfx/flags/md.svg' },
    { iso2: 'me', src: '/ClientSource/gfx/flags/me.svg' },
    { iso2: 'mk', src: '/ClientSource/gfx/flags/mk.svg' },
    { iso2: 'mt', src: '/ClientSource/gfx/flags/mt.svg' },
    { iso2: 'nl', src: '/ClientSource/gfx/flags/nl.svg' },
    { iso2: 'no', src: '/ClientSource/gfx/flags/no.svg' },
    { iso2: 'pl', src: '/ClientSource/gfx/flags/pl.svg' },
    { iso2: 'pt', src: '/ClientSource/gfx/flags/pt.svg' },
    { iso2: 'ro', src: '/ClientSource/gfx/flags/ro.svg' },
    { iso2: 'ru', src: '/ClientSource/gfx/flags/ru.svg' },
    { iso2: 'rs', src: '/ClientSource/gfx/flags/rs.svg' },
    { iso2: 'si', src: '/ClientSource/gfx/flags/si.svg' },
    { iso2: 'sk', src: '/ClientSource/gfx/flags/sk.svg' },
    { iso2: 'sm', src: '/ClientSource/gfx/flags/sm.svg' },
    { iso2: 'tr', src: '/ClientSource/gfx/flags/tr.svg' },
    { iso2: 'ua', src: '/ClientSource/gfx/flags/ua.svg' },
    { iso2: 'va', src: '/ClientSource/gfx/flags/va.svg' },
    { iso2: 'xk', src: '/ClientSource/gfx/flags/xk.svg' },
  ];

  let Translations: (key: string, defaultValue?: string) => string;
  Translations = makeTranslator(props.translations);

  function isMobileDevice() {
    if (typeof navigator !== 'undefined' && navigator.userAgent !== 'undefined') {
      return /Mobi|Android|iPhone|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    }
    return false;
  }

  const [isOpen, setIsOpen] = React.useState<boolean>(true);
  const [currentTick, setCurrentTick] = React.useState<number>(0);
  const [timeCutOff, setTimecutOff] = React.useState<Date>(new Date());
  const [swishFromThisUnit, setSwishFromThisUnit] = React.useState<boolean>(isMobileDevice());
  const [transactionId, setTransactionId] = React.useState<string | undefined | null>(props.transactionId);
  const [pollLimitReached, setPollLimitReached] = React.useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = React.useState<string>('+46');
  const [swishMUrl, setSwishMUrl] = React.useState<string>('');
  const [invalidNumber, setInvalidNumber] = React.useState<boolean>(false);
  const [showSpinner, setShowSpinner] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const countries = defaultCountries.filter((country) => {
    const { iso2 } = parseCountry(country);
    return countryList.find((x) => x.iso2 === iso2);
  });

  useEffect(() => {
    if (props.transactionId && !props.preSwishRedirect) {
      setPollLimit();
    }
  }, []);

  useEffect(() => {
    if (isOpen && transactionId) {
      if (document.visibilityState == 'visible') {
        if (currentTick % 2 == 0) {
          pollPayment();
        }
      }
      setTimeout(() => {
        if (currentTick >= maxTicks || timeCutOff.getTime() < new Date().getTime()) {
          setPollLimitReached(true);
          setTransactionId(null);
          window.location.href = location
            .toString()
            .replace(location.search, `?ansokanId=${props.ansokanId}&betalfel=true`);
        } else {
          setCurrentTick((count) => count + 1);
        }
      }, 1000);
    }
  }, [currentTick, isOpen]);

  useEffect(() => {
    if (swishFromThisUnit && swishMUrl) {
      window.location.href = swishMUrl;
    }
  }, [swishMUrl]);

  const setPollLimit = () => {
    const date = new Date();
    date.setSeconds(date.getSeconds() + maxTicks);
    setTimecutOff(date);
  };

  const isValidPhoneNumber = (phoneNumber: string) => {
    const pattern = /^[+]{1}(?:[0-9\-\\(\\)\\/.]\s?){6,15}[0-9]{1}$/;
    if (!phoneNumber) {
      return false;
    }
    if (pattern.test(phoneNumber)) {
      return true;
    } else {
      return false;
    }
  };

  const pollPayment = () => {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    doPost(
      `${props.pollLink}`,
      JSON.stringify({ ansokanType: props.ansokanType, id: transactionId, ansokan: props.ansokanId }),
      headers,
    ).then((data: PollResponse) => {
      if (data.status === PaymentStatus.Success || data.status === PaymentStatus.Failed) {
        window.location.href = data.redirectUrl;
      }
    });
  };

  const regPayment = () => {
    if (!swishFromThisUnit && !validatePhoneNumber()) {
      return;
    }
    setErrorMessage('');
    setShowSpinner(true);
    Promise.all([
      doGet<Boolean>('/_api/healthcheck'),
      doGet<Boolean>(`${props.validationPath}?Id=${props.ansokanId}`),
    ]).then((responses) => {
      const [healthCheckResponse, validationResponse] = responses;

      if (!healthCheckResponse || healthCheckResponse instanceof ResponseError) {
        props.onHealthCheckFail();
        setShowSpinner(false);
        setIsOpen(false);
        return;
      }

      if (!validationResponse || validationResponse instanceof ResponseError) {
        props.onValidationFail((validationResponse as ResponseError).body.errorMessage);
        setShowSpinner(false);
        setIsOpen(false);
        return;
      }

      const headers = new Headers({ 'Content-Type': 'application/json' });
      doPost(
        props.swishPaymentLink,
        JSON.stringify({
          ansokanType: props.ansokanType,
          ansokanId: props.ansokanId,
          betalsatt: swishFromThisUnit ? SwishM : Swish,
          nummer: phoneNumber,
          eduLoc: props.educationLocation,
          specialistbevisTyp: props.specialistBevisTyp,
          completedSupplementaryEducation: props.completedSupplementaryEducation,
          paidAssessmentFeeEarlier: props.paidAssessmentFeeEarlier,
        }),
        headers,
      ).then((response: ISwishResponse) => {
        setShowSpinner(false);
        if (response.error && response.errorMessage) {
          setErrorMessage(response.errorMessage);
        } else if (response.error) {
          window.location.href = response.redirectUrl;
        } else {
          setPollLimit();
          setTransactionId(response.transactionId);
          setCurrentTick(1);
          if (response.swishUrl && swishFromThisUnit) {
            setSwishMUrl(response.swishUrl);
          }
        }
      });
    });
  };

  const changeSwishType = () => {
    setSwishFromThisUnit(!swishFromThisUnit);
  };

  const getTimeString = () => {
    var seconds = Math.floor((timeCutOff.getTime() - new Date().getTime()) / 1000);
    if (seconds < 0) {
      return '00:00';
    }
    const minutes = Math.floor(seconds / 60);
    const secondsLeft = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secondsLeft).padStart(2, '0')}`;
  };

  const stripTrunkPrefix = (number: string, country?: ParsedCountry) => {
    if (!country) {
      return number;
    }
    const prefix = `+${country.dialCode}`;
    if (number.startsWith(`${prefix}0`)) {
      return prefix + number.slice(prefix.length + 1);
    }
    return number;
  };

  const validatePhoneNumber = () => {
    const valid = isValidPhoneNumber(phoneNumber);
    setInvalidNumber(!valid);
    return valid;
  };

  const handleChangeNumber = (number: string, meta?: { country: ParsedCountry }) => {
    const normalizedNumber = stripTrunkPrefix(number, meta?.country);
    setPhoneNumber(normalizedNumber);
    setErrorMessage('');
    setInvalidNumber(false);
  };

  return (
    <Dialog isOpen={isOpen}>
      {!transactionId ? (
        <>
          <div className="header">
            <img width="120" src="/ClientSource/gfx/swish_logotype.svg" alt="Swish logga" />
          </div>

          <button onClick={props.onDialogClose} className="close">
            <Icon isAriaHidden={false} ariaLabel="Stäng" iconType="ic_close_white_24px" />
          </button>

          <div className="swish-dialog-section">
            {!swishFromThisUnit ? (
              <div>
                <label htmlFor="phoneNumber">{Translations('phoneNumberLabel')}</label>
                <PhoneInput
                  flags={countryList}
                  name="phoneNumber"
                  aria-label={Translations('phoneNumberLabel')}
                  countries={countries}
                  preferredCountries={['se']}
                  defaultCountry="se"
                  forceDialCode
                  value={phoneNumber}
                  onChange={(phone, meta) => handleChangeNumber(phone, meta)}
                  inputProps={{ onBlur: validatePhoneNumber }}
                />
                {invalidNumber && (
                  <span className="error-message" role="alert">
                    <span>{Translations('phoneNumberFormatError')}</span>
                  </span>
                )}
                {errorMessage && (
                  <span className="error-message" role="alert">
                    <span>{errorMessage}</span>
                  </span>
                )}
              </div>
            ) : (
              <p>{Translations('swishOnthisUnitText')}</p>
            )}
          </div>

          <div className="swish-type-wrapper swish-dialog-section">
            <span tabIndex={0} className="swish-type-link" onClick={changeSwishType} role="button">
              {swishFromThisUnit ? Translations('notOnThisUnit') : Translations('onThisUnit')}
            </span>
          </div>
          <div>
            <button
              disabled={(!swishFromThisUnit && invalidNumber) || showSpinner}
              className="button relative swish-pay-button"
              type="button"
              onClick={regPayment}>
              {props.header}
              {showSpinner && <LoadingSpinner cssClasses="button-spinner" />}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="header">
            <img width="120" src="/ClientSource/gfx/swish_logotype.svg" alt="Swish-logo" />
            {props.preSwishRedirect && <h3>{Translations('waitingForPaymentHeader')}</h3>}
            {!props.preSwishRedirect && <h3>{Translations('waitingForPaymentHeaderAfterRedirect')}</h3>}
          </div>
          <div>
            {pollLimitReached && props.preSwishRedirect && <p>{Translations('pollLimitReached')}</p>}
            {pollLimitReached && !props.preSwishRedirect && <p>{Translations('pollLimitReachedAfterRedirect')}</p>}
            {!pollLimitReached && !props.preSwishRedirect && <p>{Translations('waitingForPaymentText')}</p>}
            {!pollLimitReached && props.preSwishRedirect && (
              <>
                <p>{Translations('openSwishText')}</p>
                <p>{getTimeString()}</p>
              </>
            )}
            {!pollLimitReached && !props.preSwishRedirect && <LoadingSpinner darkColor={true} />}
          </div>
        </>
      )}
    </Dialog>
  );
}
